import getReducerStateWithLockingSystemCharges from "./utils/getReducerStateWithLockingSystemCharges"
import formatPrice from "@secureo/common/utils/forex/formatPrice"

import {
	LockingSystemPricePortions,
	LockingSystemType,
	LockingSystemMatrixType,
	LockingSystemComponent,
	LockingSystemComponentsByUuid,
	LockingSystemCharges,
} from "@secureo/common/typings/LockingSystem"
import { CountryCode } from "@secureo/common/typings/CountryCode"

export * from "./actions"

export interface LockingSystemReducerState extends LockingSystemPricePortions {
	isLoading: boolean
	isDebug: boolean
	isFrozen: boolean
	hasUnsavedChanges: boolean
	shoppingListId?: string
	shoppingListVersion?: number
	countryCode: CountryCode
	locale: string

	lockingSystemType: LockingSystemType
	defaultLockingComponentSku: string
	defaultLockingComponent: LockingSystemComponent
	defaultKeyComponentSku: string
	defaultKeyComponent: LockingSystemComponent

	/**
	 * Custom name, defined by customer
	 */
	name: string
	/**
	 * Key of locking system category
	 */
	system: string

	// Matrix
	matrixType: LockingSystemMatrixType
	/**
	 * Matches visual matrix, true means the key in the column locks the cylinder in the row
	 */
	matrix: boolean[][]
	/**
	 * Rows
	 */
	lockingComponentsUuids: string[]
	/**
	 * Columns
	 */
	keyComponentsUuids: string[]

	/**
	 * Components
	 */
	componentsByUuid: LockingSystemComponentsByUuid
	charges?: LockingSystemCharges

	// Price
	taxRate: number

	// UI
	isQuickStartOpen: boolean
}

interface LockingSystemReducerAction {
	type:
		| "RESET_STATE"
		| "SET_IS_LOADING"
		| "ADD_INITIAL_COMPONENT"
		| "ADD_LOCK_COMPONENT"
		| "ADD_KEY_COMPONENT"
		| "DELETE_LOCK_COMPONENTS"
		| "DELETE_KEY_COMPONENTS"
		| "SET_CELL_VALUES"
		| "SET_COMPONENT_NAME"
		| "SET_COMPONENT_QUANTITY"
		| "SET_SYSTEM_NAME"
		| "SET_COMPONENT_CONFIGURATIONS"
		| "SET_COMPONENT_CYLINDER_CONFIGURATION_VARIANT"
		| "SET_COMPONENT_PRODUCT"
		| "SET_UPDATED_COMPONENT"
		| "SET_SPECIAL_MATRIX_TYPE"
		| "MOVE_COMPONENT"
		| "TOGGLE_ROW_SELECTIONS"
		| "TOGGLE_COLUMN_SELECTIONS"
		| "TOGGLE_QUICK_START_IS_OPEN"
		| "SAVE_LOCKING_SYSTEM"
		| "LOAD_SAVED_LOCKING_SYSTEM"
	payload?: any
}

export type DispatchLockingSystemReducer = (action: LockingSystemReducerAction) => void

const reducer = (state: LockingSystemReducerState, action: LockingSystemReducerAction) => {
	const { type, payload } = action

	if (state.isDebug) console.info(action)

	switch (type) {
		case "RESET_STATE":
			return payload

		case "LOAD_SAVED_LOCKING_SYSTEM":
			return getReducerStateWithLockingSystemCharges(payload)

		case "SET_IS_LOADING":
			return {
				...state,
				isLoading: payload,
			}

		case "SET_COMPONENT_QUANTITY":
		case "ADD_INITIAL_COMPONENT":
		case "ADD_LOCK_COMPONENT":
		case "ADD_KEY_COMPONENT":
		case "DELETE_LOCK_COMPONENTS":
		case "DELETE_KEY_COMPONENTS":
		case "SET_CELL_VALUES":
		case "SET_COMPONENT_CYLINDER_CONFIGURATION_VARIANT":
		case "SET_COMPONENT_PRODUCT":
		case "SET_UPDATED_COMPONENT":
		case "SET_SPECIAL_MATRIX_TYPE":
		case "MOVE_COMPONENT":
		case "TOGGLE_ROW_SELECTIONS":
		case "TOGGLE_COLUMN_SELECTIONS":
			return getReducerStateWithLockingSystemCharges({
				...state,
				...payload,
			})

		case "SAVE_LOCKING_SYSTEM":
			return {
				...state,
				...payload,
			}

		case "SET_COMPONENT_NAME":
			return {
				...state,
				componentsByUuid: {
					...state.componentsByUuid,
					[payload.uuid]: {
						...state.componentsByUuid[payload.uuid],
						name: payload.name,
					},
				},
			}

		case "SET_COMPONENT_CONFIGURATIONS":
			return {
				...state,
				componentsByUuid: {
					...state.componentsByUuid,
					[payload.uuid]: {
						...state.componentsByUuid[payload.uuid],
						...payload,
					},
				},
			}

		case "SET_SYSTEM_NAME":
			return {
				...state,
				name: payload,
			}

		case "TOGGLE_QUICK_START_IS_OPEN":
			return {
				...state,
				isQuickStartOpen: !state.isQuickStartOpen,
			}

		default:
			return state
	}
}

const printSeparator = () => {
	console.info("=".repeat(30))
}

const printChargesTable = (reducerState: LockingSystemReducerState) => {
	const {
		charges,
		lockingComponentsUuids,
		keyComponentsUuids,
		componentsByUuid,
		totalGrossPriceCents,
	} = reducerState

	const chargesTable = [...lockingComponentsUuids, ...keyComponentsUuids].reduce(
		(chargesTable, uuid) => {
			const {
				type,
				componentName,
				quantity,
				pricePortions: {
					productSingleGrossPriceCents,
					totalConfigurationsGrossPriceCentsPerProductQuantity,
				},
				singleGrossPriceCents,
				totalGrossPriceCents,
			} = componentsByUuid[uuid]
			const { totalGrossChargeCents, freeIncludedQuantity = 0 } = charges.byUuid[uuid]
			const index = (type === "lock" ? lockingComponentsUuids : keyComponentsUuids).findIndex(
				(componentUuid) => componentUuid === uuid,
			)

			const tableRow = {
				component: `${
					type === "lock" ? "Zylinder" : type === "key" ? "Schlüsselgruppe" : type
				} #${index + 1}`,
				componentName,

				productSingle: formatPrice(productSingleGrossPriceCents),
				configurationsSingle: formatPrice(
					totalConfigurationsGrossPriceCentsPerProductQuantity,
				),
				componentSingle: formatPrice(singleGrossPriceCents),
				quantity,
				freeIncludedKeyQuantity: freeIncludedQuantity,
				total: formatPrice(totalGrossPriceCents),
				totalCharges: formatPrice(totalGrossChargeCents),
				totalWithCharges: formatPrice(totalGrossPriceCents + totalGrossChargeCents),
			}

			return [...chargesTable, tableRow]
		},
		[],
	)

	const { numberOfFreeIncludedKeys, totalGrossChargeCents } = charges

	console.info("%cCharges", "color:salmon")
	console.table([
		...chargesTable,
		{
			component: "TOTAL",
			freeIncludedKeyQuantity: numberOfFreeIncludedKeys,
			total: formatPrice(totalGrossPriceCents),
			totalCharges: formatPrice(totalGrossChargeCents),
			totalWithCharges: formatPrice(totalGrossPriceCents + totalGrossChargeCents),
		},
	])
}

const debuggableReducer = (
	state: LockingSystemReducerState,
	action: LockingSystemReducerAction,
) => {
	if (state.isDebug) {
		printSeparator()
		console.info("%cReducer State Update", "color:salmon")
	}

	const updatedState = reducer(state, action)

	if (state.isDebug) {
		console.info(updatedState)
		printSeparator()
		printChargesTable(updatedState)
		printSeparator()
	}

	return updatedState
}

export default debuggableReducer
